/**
 * Create or restore view. Two tabs: a new wallet in four steps, each a template (create-words-template,
 * create-confirm-template, create-password-template, create-done-template), and restoring from a
 * recovery phrase (create-restore-template).
 */

import ko from 'knockout';
import { generateMnemonic, validateMnemonic, normalizeMnemonic, validatePassword } from 'streembit-bc-sdk';
import { ModelBase } from './base';
import type { WalletModel } from './wallet';
import { createWallet, requireSession } from '../session';
import { downloadWalletFile } from '../platform';

export type CreateTab = 'new' | 'restore';

const CONFIRM_WORD_COUNT = 3;

/** One of the words the user is asked to confirm from the written copy. */
export interface ConfirmWord {
    index: number;
    label: string;
    value: ko.Observable<string>;
}

function pickConfirmIndexes(): number[] {
    const indexes = new Set<number>();
    const random = new Uint32Array(CONFIRM_WORD_COUNT * 4);
    crypto.getRandomValues(random);
    let i = 0;
    while (indexes.size < CONFIRM_WORD_COUNT && i < random.length) {
        indexes.add(random[i++] % 12);
    }
    return [...indexes].sort((a, b) => a - b);
}

export class CreateModel extends ModelBase {
    /** True while the create or restore flow is shown in place of the Wallet tab's start blocks. */
    readonly active = ko.observable(false);
    readonly tab = ko.observable<CreateTab>('new');

    // new wallet
    readonly mnemonic = ko.observable('');
    readonly words = ko.pureComputed(() => this.mnemonic() ? this.mnemonic().split(' ') : []);
    readonly saved = ko.observable(false);
    readonly confirmWords = ko.observableArray<ConfirmWord>([]);
    readonly password = ko.observable('');
    readonly password2 = ko.observable('');
    readonly newAddress = ko.observable('');

    // restore
    readonly restoreMnemonic = ko.observable('');
    readonly restorePassword = ko.observable('');
    readonly restorePassword2 = ko.observable('');

    readonly busy = ko.observable(false);

    constructor(private readonly wallet: WalletModel) {
        super();
        this.template('create-words-template');
    }

    /** Opens the flow on the tab asked for, with a fresh phrase for a new wallet. */
    activate(tab: CreateTab): void {
        this.hideErrors();
        this.tab(tab);
        if (!this.mnemonic()) this.regenerate();
        this.active(true);
    }

    /** Leaves the flow; a phrase that was shown but not used is dropped. */
    close(): void {
        this.hideErrors();
        this.active(false);
        this.mnemonic('');
        this.confirmWords([]);
        this.saved(false);
        this.template('create-words-template');
    }

    selectTab(tab: CreateTab): void {
        this.hideErrors();
        this.tab(tab);
    }

    regenerate(): void {
        this.mnemonic(generateMnemonic());
        this.saved(false);
        this.confirmWords([]);
        this.template('create-words-template');
    }

    next(): void {
        this.hideErrors();
        this.confirmWords(pickConfirmIndexes().map(index => ({ index, label: `Word ${index + 1}`, value: ko.observable('') })));
        this.template('create-confirm-template');
    }

    back(): void {
        this.hideErrors();
        this.template('create-words-template');
    }

    confirm(): void {
        this.hideErrors();
        const words = this.words();
        const wrong = this.confirmWords().filter(w => normalizeMnemonic(w.value()) !== words[w.index]);
        if (wrong.length) {
            this.displayErrors(`Word ${wrong.map(w => w.index + 1).join(', ')} does not match the phrase. Check your written copy.`);
            return;
        }
        this.template('create-password-template');
    }

    async create(): Promise<void> {
        this.hideErrors();
        this.busy(true);
        try {
            const session = await this.finish(this.mnemonic(), this.password(), this.password2());
            this.password('');
            this.password2('');
            this.mnemonic('');
            this.confirmWords([]);
            this.newAddress(session.account.address);
            this.template('create-done-template');
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    download(): void {
        const s = requireSession();
        downloadWalletFile(s.file, s.account.address);
    }

    async restore(): Promise<void> {
        this.hideErrors();
        const mnemonic = normalizeMnemonic(this.restoreMnemonic());
        if (!validateMnemonic(mnemonic)) {
            this.displayErrors('The recovery phrase is not valid: check the spelling and the order of the words.');
            return;
        }
        this.busy(true);
        try {
            await this.finish(mnemonic, this.restorePassword(), this.restorePassword2());
            this.restoreMnemonic('');
            this.restorePassword('');
            this.restorePassword2('');
            this.close();
            this.wallet.notice('The wallet was restored and is unlocked.');
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    /** Checks the password, seals the phrase into a wallet file kept in this browser and unlocks it. */
    private async finish(mnemonic: string, password: string, repeat: string) {
        if (password !== repeat) throw new Error('The two passwords differ');
        const check = validatePassword(password);
        if (!check.isValid) throw new Error(check.error ?? 'Invalid password');
        const chain = this.wallet.chain();
        if (!chain) throw new Error('The node is not connected');
        const session = await createWallet(mnemonic, password, chain.network);
        await this.wallet.reloadStored();
        this.wallet.startSession(session);
        return session;
    }
}
